import { capitalize } from "@app/string-utils";
import type { DataTableSort } from "@app/ui/shared/datatable";
import {
  AllCommunityModule,
  type ColDef,
  type FirstDataRenderedEvent,
  type GridApi,
  type GridOptions,
  type GridSizeChangedEvent,
  type GridState,
  type ITooltipParams,
  type PaginationChangedEvent,
  type RowClickedEvent,
  type SortChangedEvent,
  type SortDirection,
  themeQuartz,
} from "ag-grid-community";
import {
  AgGridProvider,
  AgGridReact,
  type CustomHeaderProps,
} from "ag-grid-react";
import cn from "classnames";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type React from "react";
import { useSearchParams } from "react-router-dom";
import { ButtonIcon } from "../button";
import {
  IconArrowDown,
  IconArrowLeft,
  IconArrowRight,
  IconArrowUp,
  IconInfo,
  IconSwitchVertical,
} from "../icons";
import { LoadingSpinner } from "../loading";

export interface ControlsDataTableHeaderComponentParams {
  leadingIcon?: React.ReactNode;
  labelClassName?: string;
  containerClassName?: string;
}

const QUERY_PARAM_PREFIX = "controls";

const aptibleTheme = themeQuartz;

const LoadingOverlayComponent = () => {
  return (
    <div className="flex items-center justify-center h-full pt-12">
      <LoadingSpinner variant="lg" />
    </div>
  );
};

const SortIndicator = ({ sort }: { sort: SortDirection | undefined }) => (
  <span className="ag-sort-indicator-container">
    <span
      className={cn("ag-sort-indicator-icon ag-sort-ascending-icon", {
        "ag-hidden": sort !== "asc",
      })}
    >
      <IconArrowUp
        variant="sm"
        className="h-3 w-3"
        strokeWidth="3"
        color="currentColor"
      />
    </span>
    <span
      className={cn("ag-sort-indicator-icon ag-sort-descending-icon", {
        "ag-hidden": sort !== "desc",
      })}
    >
      <IconArrowDown
        variant="sm"
        className="h-3 w-3"
        strokeWidth="3"
        color="currentColor"
      />
    </span>
    <span
      className={cn("ag-sort-indicator-icon ag-sort-none-icon", {
        "ag-hidden": sort != null,
      })}
    >
      <IconSwitchVertical
        variant="sm"
        className="h-4 w-4"
        strokeWidth="2"
        color="currentColor"
      />
    </span>
  </span>
);

const HeaderComponent = <T,>({
  displayName,
  column,
  progressSort,
  enableSorting,
}: CustomHeaderProps<T>) => {
  const colDef = column.getColDef() as ColDef<T>;
  const tooltip = colDef.headerTooltip;
  const headerParams = (colDef.headerComponentParams ??
    {}) as ControlsDataTableHeaderComponentParams;
  const [sort, setSort] = useState<SortDirection | undefined>(column.getSort());

  useEffect(() => {
    const onSortChanged = () => setSort(column.getSort());
    column.addEventListener("sortChanged", onSortChanged);
    return () => column.removeEventListener("sortChanged", onSortChanged);
  }, [column]);

  const onSort = (
    event:
      | React.MouseEvent<HTMLDivElement>
      | React.KeyboardEvent<HTMLDivElement>,
  ) => {
    if (!enableSorting) return;
    progressSort?.(event.shiftKey);
  };

  return (
    <div
      className={cn("ag-cell-label-container", {
        "ag-header-cell-sorted-asc": sort === "asc",
        "ag-header-cell-sorted-desc": sort === "desc",
      })}
      onClick={onSort}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSort(event);
        }
      }}
      role={enableSorting ? "button" : undefined}
      tabIndex={enableSorting ? 0 : undefined}
      aria-label={enableSorting ? `Sort by ${displayName}` : undefined}
    >
      <div
        className={cn(
          "ag-header-cell-label",
          headerParams.leadingIcon != null &&
            "flex min-w-0 items-center justify-start gap-1.5",
          headerParams.containerClassName,
        )}
      >
        {headerParams.leadingIcon ? (
          <span className="inline-flex shrink-0 items-center justify-center">
            {headerParams.leadingIcon}
          </span>
        ) : null}
        <span
          className={cn(
            "ag-header-cell-text min-w-0",
            headerParams.labelClassName,
          )}
        >
          {displayName}
        </span>
        {tooltip && (
          <span className="ag-header-cell-tooltip-icon">
            <IconInfo variant="sm" color="currentColor" />
          </span>
        )}
        {enableSorting && <SortIndicator sort={sort} />}
      </div>
    </div>
  );
};

const TooltipComponent = <T,>({ value }: ITooltipParams<T>) => {
  return (
    <div className="shadow rounded-md break-normal text-sm text-left bg-black text-white px-3 py-2 w-96 transition-none">
      {value}
    </div>
  );
};

const baseGridOptions: GridOptions = {
  theme: aptibleTheme,
  loadingOverlayComponent: LoadingOverlayComponent,
  defaultColDef: {
    autoHeight: true,
    sortingOrder: ["asc", "desc"],
    unSortIcon: true,
    tooltipComponent: TooltipComponent,
    headerComponent: HeaderComponent,
    lockPinned: true,
  },
  tooltipShowDelay: 250,
  tooltipInteraction: true,
  enableCellTextSelection: true,
  animateRows: false,
  animateColumnResizing: false,
  suppressColumnMoveAnimation: true,
  suppressDragLeaveHidesColumns: true,
  domLayout: "autoHeight",
  colResizeDefault: "shift",
  suppressScrollOnNewData: true,
};

const PaginationControls = ({
  isLoading,
  currentPage,
  totalPages,
  totalResults,
  getPreviousPage,
  getNextPage,
}: {
  isLoading?: boolean;
  currentPage: number;
  totalPages?: number;
  totalResults?: number;
  getPreviousPage: () => void;
  getNextPage: () => void;
}) => {
  return (
    <div className="flex flex-row items-center gap-2 text-gray-500">
      <div className="flex items-center gap-2 whitespace-nowrap">
        Page {currentPage} of{" "}
        <LoadingSpinner
          show={isLoading}
          className="inline-block"
          variant="sm"
        />
        {!isLoading && (totalPages || 1)}
      </div>
      <div className="flex flex-row items-center gap-2">
        <ButtonIcon
          variant="white"
          icon={<IconArrowLeft color="#111920" variant="sm" />}
          size="xs"
          disabled={isLoading || currentPage <= 1}
          onClick={(e) => {
            e.preventDefault();
            getPreviousPage();
          }}
        />
        <ButtonIcon
          variant="white"
          icon={<IconArrowRight color="#111920" variant="sm" />}
          size="xs"
          disabled={isLoading || currentPage >= (totalPages ?? 0)}
          onClick={(e) => {
            e.preventDefault();
            getNextPage();
          }}
        />
        <div className="whitespace-nowrap">
          <div className="flex items-center gap-2">
            <LoadingSpinner
              show={isLoading}
              className="inline-block"
              variant="sm"
            />
            {!isLoading && (totalResults ?? 0)} results
          </div>
        </div>
      </div>
    </div>
  );
};

export interface ControlsDataTableProps<T> {
  rowData: T[];
  colDefs: ColDef<T>[];
  getRowId?: (data: T) => string;
  onRowClick?: (data: T) => void;
  getRowClassName?: (data: T) => string | undefined;
  isLoading?: boolean;
  className?: string;
  pageSize?: number;
  defaultSort?: DataTableSort;
  /**
   * Sort used on first mount. When `null`, ignore prefixed URL sort params and use
   * `defaultSort` (caller validated URL sort as invalid for the current layout).
   */
  initialSort?: DataTableSort | null;
}

const getQueryParamName = (name: "page" | "sortBy" | "sortDir") => {
  return [QUERY_PARAM_PREFIX, capitalize(name)].join("");
};

export function ControlsDataTable<T>({
  rowData,
  colDefs,
  getRowId,
  onRowClick,
  getRowClassName,
  isLoading = false,
  className,
  pageSize = 50,
  defaultSort,
  initialSort,
}: ControlsDataTableProps<T>) {
  const gridRef = useRef<AgGridReact<T>>(null);

  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortDir, setSortDir] = useState<SortDirection | undefined>(undefined);
  const [totalResults, setTotalResults] = useState<number | undefined>(
    undefined,
  );
  const [totalPages, setTotalPages] = useState<number | undefined>(undefined);

  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = getQueryParamName("page");
  const pageParamValue = Number.parseInt(
    searchParams.get(pageParam) || "1",
    10,
  );
  const sortByParam = getQueryParamName("sortBy");
  const sortByParamValue = searchParams.get(sortByParam) || undefined;
  const sortDirParam = getQueryParamName("sortDir");
  const sortDirParamValue = searchParams.get(sortDirParam) as
    | NonNullable<SortDirection>
    | undefined;

  const columnStructureKey = useMemo(
    () =>
      colDefs
        .map((col) => {
          const id = col.colId ?? col.field;
          return typeof id === "string" ? id : "";
        })
        .join("|"),
    [colDefs],
  );

  const initialGridState = useMemo<GridState | undefined>(() => {
    const page = pageParamValue - 1;
    const sortBy =
      initialSort === null
        ? defaultSort?.field
        : (initialSort?.field ?? sortByParamValue ?? defaultSort?.field);
    const sortDir =
      initialSort === null
        ? (defaultSort?.direction as SortDirection | undefined)
        : (initialSort?.direction ??
          sortDirParamValue ??
          (defaultSort?.direction as SortDirection | undefined));

    return {
      pagination: {
        page: page,
        pageSize: pageSize,
      },
      sort:
        sortBy && sortDir
          ? {
              sortModel: [
                { colId: sortBy, sort: sortDir as NonNullable<SortDirection> },
              ],
            }
          : undefined,
      partialColumnState: true,
    };
  }, [
    pageParamValue,
    sortByParamValue,
    sortDirParamValue,
    defaultSort,
    initialSort,
    pageSize,
  ]);

  useEffect(() => {
    setPage(pageParamValue);
    setSortBy(
      initialSort === null
        ? defaultSort?.field
        : (sortByParamValue ?? initialSort?.field),
    );
    setSortDir(
      initialSort === null
        ? (defaultSort?.direction as SortDirection | undefined)
        : (sortDirParamValue ??
            (initialSort?.direction as SortDirection | undefined)),
    );
  }, []);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);

        Object.entries({
          [pageParam]: page > 1 ? page.toString() : "",
          [sortByParam]: sortBy || "",
          [sortDirParam]: sortDir || "",
        }).forEach(([key, value]) => {
          if (value !== "") {
            next.set(key, value.toString());
          } else {
            next.delete(key);
          }
        });

        return next;
      },
      { replace: true },
    );
  }, [page, sortBy, sortDir]);

  // When scope changes the default status column, apply it unless the URL specifies sort.
  useEffect(() => {
    if (!defaultSort) return;
    if (initialSort) return;
    if (initialSort !== null && (sortByParamValue || sortDirParamValue)) return;

    const frame = requestAnimationFrame(() => {
      const api = gridRef.current?.api;
      if (!api) return;

      api.applyColumnState({
        state: [{ colId: defaultSort.field, sort: defaultSort.direction }],
        defaultState: { sort: null },
      });
      setSortBy(defaultSort.field);
      setSortDir(defaultSort.direction);
    });

    return () => cancelAnimationFrame(frame);
  }, [
    defaultSort,
    initialSort,
    sortByParamValue,
    sortDirParamValue,
    columnStructureKey,
  ]);

  const fitColumnsAndResetHeights = useCallback((api: GridApi) => {
    api.sizeColumnsToFit();
    requestAnimationFrame(() => {
      api.resetRowHeights();
    });
  }, []);

  const gridOptions = useMemo<GridOptions<T>>(() => {
    return {
      ...baseGridOptions,
      onGridSizeChanged: ({ api }: GridSizeChangedEvent) => {
        fitColumnsAndResetHeights(api);
      },
      loading: isLoading,
      rowData: rowData,
      columnDefs: colDefs,
      getRowId: getRowId ? (params) => getRowId(params.data as T) : undefined,
      getRowClass: getRowClassName
        ? (params) => getRowClassName(params.data as T)
        : undefined,
      suppressHorizontalScroll: isLoading || !rowData.length,
      pagination: true,
      paginationPageSize: pageSize,
      suppressPaginationPanel: true,
      initialState: initialGridState,
      onFirstDataRendered: ({ api }: FirstDataRenderedEvent<T>) => {
        setTotalResults(api.getDisplayedRowCount());
        setTotalPages(Math.ceil(api.getDisplayedRowCount() / pageSize));
        fitColumnsAndResetHeights(api);
        setPage((prev) => {
          return Math.min(
            Math.max(1, prev),
            Math.ceil(api.getDisplayedRowCount() / pageSize),
          );
        });
      },
      onRowDataUpdated: ({ api }: { api: GridApi }) => {
        setTotalResults(api.getDisplayedRowCount());
        setTotalPages(Math.ceil(api.getDisplayedRowCount() / pageSize));
        requestAnimationFrame(() => {
          api.resetRowHeights();
        });
      },
      onPaginationChanged: ({ api, newPage }: PaginationChangedEvent<T>) => {
        if (newPage) {
          setPage(api.paginationGetCurrentPage() + 1);
        }
      },
      onSortChanged: ({ api }: SortChangedEvent<T>) => {
        const sortedCol = api.getColumnState().find((c) => c.sort);
        if (sortedCol) {
          setSortBy(sortedCol.colId);
          setSortDir(sortedCol.sort as SortDirection);
        } else {
          setSortBy(undefined);
          setSortDir(undefined);
        }
      },
      onRowClicked: ({ data }: RowClickedEvent<T>) => {
        if (!data) return;
        onRowClick?.(data);
      },
    };
  }, [
    rowData,
    colDefs,
    isLoading,
    pageSize,
    getRowId,
    onRowClick,
    getRowClassName,
    fitColumnsAndResetHeights,
    initialGridState,
  ]);

  return (
    <div className="flex flex-col gap-2">
      <PaginationControls
        isLoading={isLoading}
        currentPage={page}
        totalPages={totalPages}
        totalResults={totalResults}
        getPreviousPage={() =>
          gridRef.current?.api.paginationGoToPreviousPage()
        }
        getNextPage={() => gridRef.current?.api.paginationGoToNextPage()}
      />
      <AgGridProvider modules={[AllCommunityModule]}>
        <div
          className={cn("min-w-0 w-full h-full ag-theme-aptible", className)}
        >
          <AgGridReact<T>
            key={columnStructureKey}
            ref={gridRef}
            {...gridOptions}
          />
        </div>
      </AgGridProvider>
    </div>
  );
}
